import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider } from '@/contexts/AuthContext';
import { RefreshProvider } from '@/contexts/RefreshContext';
import { ToastProvider } from '@/components/ui/Toast';
import { AppShell } from '@/components/layout/AppShell';
import { useAuth } from '@/hooks/useAuth';

import LoginPage from '@/pages/LoginPage';
import NotFoundPage from '@/pages/NotFoundPage';
import AutomationQueuePage from '@/pages/AutomationQueuePage';
import AutomationDetailPage from '@/pages/AutomationDetailPage';

const AdminUsersPage = lazy(() => import('@/pages/AdminUsersPage'));
const AdminAuditPage = lazy(() => import('@/pages/AdminAuditPage'));

function FullScreenLoader() {
  return (
    <div className="flex h-screen items-center justify-center gap-8 bg-neutral-50 text-neutral-500">
      <Loader2 className="h-20 w-20 animate-spin" />
      <span>Loading…</span>
    </div>
  );
}

function RouteFallback() {
  return (
    <div className="flex h-full min-h-[40vh] items-center justify-center text-neutral-500">
      <Loader2 className="h-8 w-8 animate-spin" />
    </div>
  );
}

function ProtectedShell({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <AppShell>{children}</AppShell>;
}

export default function App() {
  return (
    <AuthProvider>
      <RefreshProvider>
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/"
              element={
                <ProtectedShell>
                  <Navigate to="/automations/ap-purchase-invoice" replace />
                </ProtectedShell>
              }
            />
            <Route
              path="/automations/:slug"
              element={
                <ProtectedShell>
                  <AutomationQueuePage />
                </ProtectedShell>
              }
            />
            <Route
              path="/automations/:slug/:id"
              element={
                <ProtectedShell>
                  <AutomationDetailPage />
                </ProtectedShell>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedShell>
                  <Suspense fallback={<RouteFallback />}>
                    <AdminUsersPage />
                  </Suspense>
                </ProtectedShell>
              }
            />
            <Route
              path="/admin/audit"
              element={
                <ProtectedShell>
                  <Suspense fallback={<RouteFallback />}>
                    <AdminAuditPage />
                  </Suspense>
                </ProtectedShell>
              }
            />
            <Route
              path="*"
              element={
                <ProtectedShell>
                  <NotFoundPage />
                </ProtectedShell>
              }
            />
          </Routes>
        </ToastProvider>
      </RefreshProvider>
    </AuthProvider>
  );
}
